/**
 * QrScanDialog — worker-facing camera scanner for the Site QR poster
 * (Jun 2026). Accepts payloads of the form "HINESQR:<token>" and calls
 * onScan(token). Foreign QR codes are silently ignored. A skip option
 * lets the worker continue without scanning (GPS / unverified fallback).
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { QrCode, AlertTriangle } from 'lucide-react';

export default function QrScanDialog({
  open, jobTitle, title, description, skipLabel, onScan, onSkip, onClose,
}) {
  const [cameraError, setCameraError] = useState(null);
  const scannerRef = useRef(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    firedRef.current = false;
    setCameraError(null);
    let cancelled = false;
    (async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        if (cancelled) return;
        const instance = new Html5Qrcode('site-qr-reader');
        scannerRef.current = instance;
        await instance.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          (text) => {
            if (firedRef.current) return;
            const raw = String(text || '').trim();
            // Accept legacy raw payloads ("HINESQR:<token>") AND the new
            // URL posters ("https://…/clock-in?qr=<token>") so old printed
            // posters keep working.
            let token = null;
            const m = raw.match(/HINESQR:([A-Za-z0-9_-]+)/);
            if (m) {
              token = m[1];
            } else {
              try { token = new URL(raw).searchParams.get('qr'); } catch { token = null; }
            }
            if (!token) return;
            firedRef.current = true;
            onScan?.(token.trim());
          },
          () => {},
        );
      } catch (err) {
        if (!cancelled) {
          setCameraError('Camera unavailable — allow camera access in your browser, or use the option below.');
        }
      }
    })();
    return () => {
      cancelled = true;
      const inst = scannerRef.current;
      scannerRef.current = null;
      if (inst) {
        inst.stop().then(() => inst.clear()).catch(() => {});
      }
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-[95vw] sm:max-w-md" data-testid="qr-scan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-[#1E88E5]" />
            <span>{title || 'Scan the Site QR Code'}</span>
          </DialogTitle>
          <DialogDescription>
            {description || (
              <>Point your camera at the QR code posted at the job site
              {jobTitle ? <> for <span className="font-semibold text-slate-200">{jobTitle}</span></> : null}.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div
          className="relative rounded-2xl overflow-hidden"
          style={{ background: '#000', border: '1px solid #2D3748', aspectRatio: '1/1' }}
        >
          <div id="site-qr-reader" className="w-full h-full" data-testid="qr-scan-camera" />
          {cameraError && (
            <div
              className="absolute inset-0 flex items-start gap-2 p-4 text-sm"
              style={{ color: '#F0A020', background: '#0F1923' }}
              data-testid="qr-scan-camera-error"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{cameraError}</span>
            </div>
          )}
          {/* Scan frame overlay — only shown when camera is active */}
          {!cameraError && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="relative" style={{ width: '200px', height: '200px' }}>
                {/* Corner brackets */}
                <div className="absolute top-0 left-0 w-8 h-8 border-white border-t-4 border-l-4 rounded-tl-lg" />
                <div className="absolute top-0 right-0 w-8 h-8 border-white border-t-4 border-r-4 rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-8 h-8 border-white border-b-4 border-l-4 rounded-bl-lg" />
                <div className="absolute bottom-0 right-0 w-8 h-8 border-white border-b-4 border-r-4 rounded-br-lg" />
                {/* Animated scan line */}
                <div
                  className="absolute left-2 right-2 h-0.5 animate-bounce"
                  style={{
                    top: '50%',
                    background: '#60A5FA',
                    boxShadow: '0 0 8px rgba(96,165,250,0.8)',
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {onSkip && (
            <button
              type="button"
              onClick={() => onSkip?.()}
              className="w-full py-3 rounded-xl text-sm font-medium transition-all active:scale-95"
              style={{ background: '#1A2433', color: '#94A3B8', border: '1px solid #2D3748' }}
              data-testid="qr-scan-skip-btn"
            >
              {skipLabel || "Can't scan? Continue with GPS instead"}
            </button>
          )}
          <button
            type="button"
            onClick={() => onClose?.()}
            className="w-full py-2.5 rounded-xl text-sm transition-all active:scale-95"
            style={{ color: '#475569' }}
            data-testid="qr-scan-cancel-btn"
          >
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
